import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../utils/api'
import './Menu.css'
import RequestFab from '../components/RequestFab'

const CustomerMenu = () => {
    const { qrCode } = useParams()
    const navigate = useNavigate()
    const [menu, setMenu] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [selectedCategory, setSelectedCategory] = useState('All')
    const [cart, setCart] = useState([])
    const [showCart, setShowCart] = useState(false)
    const [placingOrder, setPlacingOrder] = useState(false)
    const [requestMenuOpen, setRequestMenuOpen] = useState(false)

    useEffect(() => {
        if (qrCode) {
            fetchMenu()
        }
    }, [qrCode])

    const fetchMenu = async () => {
        setLoading(true)
        setError('')
        try {
            const response = await api.get(`/menu/table/${qrCode}`)
            if (response.data.success) {
                setMenu(response.data.data)
            } else {
                setError(response.data.message || 'Failed to load menu')
            }
        } catch (err) {
            console.error('Error fetching menu:', err)
            setError(err.response?.data?.message || 'Failed to load menu. Please check the QR code.')
        } finally {
            setLoading(false)
        }
    }

    const addToCart = (item) => {
        const existingItem = cart.find(cartItem => cartItem.id === item.id)
        if (existingItem) {
            setCart(cart.map(cartItem =>
                cartItem.id === item.id
                    ? { ...cartItem, quantity: cartItem.quantity + 1 }
                    : cartItem
            ))
        } else {
            setCart([...cart, { ...item, quantity: 1 }])
        }
    }

    const removeFromCart = (itemId) => {
        setCart(cart.filter(item => item.id !== itemId))
    }

    const updateQuantity = (itemId, quantity) => {
        if (quantity <= 0) {
            removeFromCart(itemId)
        } else {
            setCart(cart.map(item =>
                item.id === itemId ? { ...item, quantity } : item
            ))
        }
    }

    const getTotalAmount = () => {
        return cart.reduce((total, item) => total + (parseFloat(item.price) * item.quantity), 0)
    }

    const handlePlaceOrder = async () => {
        if (cart.length === 0) {
            alert('Your cart is empty')
            return
        }

        setPlacingOrder(true)
        try {
            const orderData = {
                table_id: menu.table.id,
                account_id: menu.account.id,
                items: cart.map(item => ({
                    menu_item_id: item.id,
                    name: item.name,
                    price: item.price,
                    quantity: item.quantity
                })),
                total_amount: getTotalAmount(),
                table_number: menu.table.table_number
            }

            const response = await api.post('/orders/restaurant', orderData)
            if (response.data.success) {
                alert('Order placed successfully!')
                setCart([])
                setShowCart(false)
                // Optionally navigate to order confirmation page
            } else {
                alert(response.data.message || 'Failed to place order')
            }
        } catch (err) {
            console.error('Error placing order:', err)
            alert(err.response?.data?.message || 'Failed to place order. Please try again.')
        } finally {
            setPlacingOrder(false)
        }
    }

    const categories = menu ? ['All', ...menu.categories.map(c => c.name)] : []
    const filteredItems = menu && selectedCategory !== 'All'
        ? menu.items.filter(item => item.category_name === selectedCategory)
        : menu?.items || []

    if (loading) {
        return (
            <div className="container">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading menu...</p>
                </div>
            </div>
        )
    }

    if (error || !menu) {
        return (
            <div className="container">
                <div className="card">
                    <div className="empty-state">
                        <div className="empty-icon">⚠️</div>
                        <p>{error || 'Menu not found'}</p>
                        <button className="btn-primary" onClick={() => navigate('/')}>
                            Go to Home
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="container">
            <div className="page-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {menu.account.logo_url && (
                        <img
                            src={menu.account.logo_url}
                            alt={`${menu.account.name} Logo`}
                            style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.1)' }}
                        />
                    )}
                    <div>
                        <h1>{menu.account.name}</h1>
                        <p className="page-subtitle">Table {menu.table.table_number} • Browse our digital menu</p>
                    </div>
                </div>
                {cart.length > 0 && (
                    <button className="btn-primary" onClick={() => setShowCart(true)}>
                        🛒 Cart ({cart.length}) • ₹{getTotalAmount().toFixed(2)}
                    </button>
                )}
            </div>

            <div className="menu-categories">
                {categories.map(category => (
                    <button
                        key={category}
                        className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                        onClick={() => setSelectedCategory(category)}
                    >
                        {category}
                    </button>
                ))}
            </div>

            {filteredItems.length === 0 ? (
                <div className="card">
                    <div className="empty-state">
                        <div className="empty-icon">🍽️</div>
                        <p>No items in this category</p>
                    </div>
                </div>
            ) : (
                <div className="menu-items">
                    {filteredItems.map(item => (
                        <div key={item.id} className="menu-item-card">
                            {item.image_url && (
                                <div className="menu-item-image">
                                    <img src={item.image_url} alt={item.name} />
                                </div>
                            )}
                            <div className="menu-item-content">
                                <h3>{item.name}</h3>
                                {item.description && <p className="menu-item-description">{item.description}</p>}
                                <div className="menu-item-footer">
                                    <span className="menu-item-price">₹{item.price}</span>
                                    <button
                                        className="btn-primary btn-sm"
                                        onClick={() => addToCart(item)}
                                    >
                                        Add
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Cart Modal */}
            {showCart && (
                <div className="modal-overlay" onClick={() => setShowCart(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Your Cart</h2>
                            <button className="modal-close" onClick={() => setShowCart(false)}>×</button>
                        </div>
                        <div className="cart-items">
                            {cart.length === 0 ? (
                                <p>Your cart is empty</p>
                            ) : (
                                <>
                                    {cart.map(item => (
                                        <div key={item.id} className="cart-item">
                                            <div style={{ flex: 1 }}>
                                                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', color: 'var(--text-primary)' }}>{item.name}</h4>
                                                <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                                                    ₹{item.price} each
                                                </p>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                                <div className="cart-item-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.05)', padding: '0.25rem', borderRadius: '0.5rem' }}>
                                                    <button
                                                        className="btn-icon"
                                                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                                        style={{ width: '32px', height: '32px', padding: '0', minHeight: 'auto' }}
                                                    >
                                                        −
                                                    </button>
                                                    <span style={{ minWidth: '30px', textAlign: 'center', fontWeight: '600', fontSize: '1rem' }}>{item.quantity}</span>
                                                    <button
                                                        className="btn-icon"
                                                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                                        style={{ width: '32px', height: '32px', padding: '0', minHeight: 'auto' }}
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                                <div style={{ minWidth: '80px', textAlign: 'right' }}>
                                                    <p style={{ margin: 0, fontWeight: '700', fontSize: '1.1rem', color: 'var(--primary-color)' }}>
                                                        ₹{(parseFloat(item.price) * item.quantity).toFixed(2)}
                                                    </p>
                                                </div>
                                                <button
                                                    className="btn-icon delete"
                                                    onClick={() => removeFromCart(item.id)}
                                                    style={{ width: '36px', height: '36px', padding: '0', minHeight: 'auto' }}
                                                    title="Remove from cart"
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    <div className="cart-total">
                                        <h3>Total: ₹{getTotalAmount().toFixed(2)}</h3>
                                    </div>
                                    <div className="modal-actions">
                                        <button
                                            className="btn-secondary"
                                            onClick={() => setShowCart(false)}
                                        >
                                            Continue Shopping
                                        </button>
                                        <button
                                            className="btn-primary"
                                            onClick={handlePlaceOrder}
                                            disabled={placingOrder}
                                        >
                                            {placingOrder ? 'Placing Order...' : 'Place Order'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <RequestFab
                isOpen={requestMenuOpen}
                onClick={() => setRequestMenuOpen(!requestMenuOpen)}
            />
        </div>
    )
}

export default CustomerMenu

